import { mdiFileDocument } from '@mdi/js';
import { Icon } from '@mdi/react';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import { ClaimResponsibilityType } from '../../api';
import { formatDate, formatNumber } from '../../services';
import { getResponsibilityDescription, ResponsibilityBadge } from '../badge';
import { Tooltip } from '../base';
import * as S from './claim-search-result.styles';

export interface IClaimSearchResultProps {
    id?: string;
    fromDos?: string;
    throughDos?: string;
    claimResponsibilityType?: keyof typeof ClaimResponsibilityType;
    primaryPayerName?: string;
    charge?: string;
    externalId?: string;
    onClick?: () => void;
    selected?: boolean;
    disableNavigation?: boolean;
}

export const ClaimSearchResult: React.FC<IClaimSearchResultProps> = ({
    id,
    externalId,
    fromDos,
    throughDos,
    claimResponsibilityType,
    primaryPayerName,
    charge,
    selected,
    disableNavigation,
    onClick,
}) => {
    const fmtNumber = formatNumber('Currency');
    const fmtDate = formatDate('MonthDay');
    const total = charge ? Number.parseFloat(charge) : Number.NaN;
    const from = fromDos ? fmtDate(fromDos) : undefined;
    const through = throughDos ? fmtDate(throughDos) : undefined;
    const serviceDate = through && from !== through ? `${from} – ${through}` : from;
    const claimExternalId = externalId ? (externalId.length > 20 ? externalId.substr(0, 18) + "..." : externalId) : '-';

    const { accountId } = useParams<{ accountId: string }>();

    // Support app uses /{accountId}/claims/{id}, provider app uses /claims/{id}
    const claimLink = accountId ? `/${accountId}/claims/${id}` : `/claims/${id}`;

    const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        // Select the claim when clicking
        onClick?.();
        
        // Prevent navigation if disabled
        if (disableNavigation) {
            e.preventDefault();
        }
    };

    return (
        <S.Claim selected={selected}>
            <S.ClaimIcon>
                <S.ClaimLink to={claimLink} onClick={handleLinkClick}>
                    <Icon path={mdiFileDocument} size={1} />
                </S.ClaimLink>
            </S.ClaimIcon>
            <S.ClaimServiceDate>
                <S.ClaimLink to={claimLink} onClick={handleLinkClick}>{serviceDate}</S.ClaimLink>
            </S.ClaimServiceDate>
            <S.ClaimId>
                <S.ClaimLink to={claimLink} onClick={handleLinkClick}>{claimExternalId}</S.ClaimLink>
            </S.ClaimId>
            <S.ClaimStatus>
                <S.ClaimLink to={claimLink} onClick={handleLinkClick}>
                    <Tooltip
                        direction="top"
                        variant="dark"
                        title={
                            claimResponsibilityType ? (
                                <span>
                                    {getResponsibilityDescription(ClaimResponsibilityType[claimResponsibilityType])}
                                </span>
                            ) : undefined
                        }>
                        <div>
                            <ResponsibilityBadge
                                size="small"
                                responsibility={
                                    claimResponsibilityType
                                        ? ClaimResponsibilityType[claimResponsibilityType]
                                        : claimResponsibilityType
                                }
                            />
                        </div>
                    </Tooltip>
                </S.ClaimLink>
            </S.ClaimStatus>
            <S.ClaimPayer>
                <S.ClaimLink to={claimLink} onClick={handleLinkClick}>{primaryPayerName}</S.ClaimLink>
            </S.ClaimPayer>
            {!Number.isNaN(total) && (
                <S.ClaimTotal>
                    <S.ClaimLink to={claimLink} onClick={handleLinkClick}>{fmtNumber(total)}</S.ClaimLink>
                </S.ClaimTotal>
            )}
        </S.Claim>
    );
};
